import React, { useEffect, useState } from 'react';
import { BuildingHelperService } from '../../app-confi/services/building-helper.service';
import { GeolocationHelper } from '../../common/address-to-geoloc';
import { CloudStorageService } from '../../common/firebase/cloud-storage.service';
import { FirestoreService } from '../../common/firebase/firestore.service';
import { severity } from '../../common/models/severity.enum';
import { SceneManager } from '../../common/scene.manager';
import { HttpService } from '../../common/services/http.service';
import { MapService } from '../../common/services/map.service';
import { UtilityService } from '../../common/services/utility.service';
import { Building, Project, Site } from '../../confi-state';
import { environment } from '../../environment.enum';
import { setEnvKey } from '../../environments';
import { SectionJSON } from '../../models/section-json';
import { firestoreCollections } from '../../services/firestore-collections.enum';
import { ServiceManager } from '../../services/service-manager';
import { UserData } from '../../common/authentication/models/user';
import { BabylonService } from '../BuildingConf/babylon.service';
import styles from './Admin.module.css';


const getSites = async (userId: number | string, projectId: number | string): Promise<Site[]> => {
    let sites = await FirestoreService.getCollectionForUserId<Site>(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}`, userId as any,
        (error) => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens laden van kavels: "${error.message}"`, severity.error);
        });
    return sites ?? [];
}

const getProjects = async (userId: string): Promise<Project[]> => {
    const projects = await FirestoreService.getCollectionForUserId<Project>(firestoreCollections.projects, userId,
        (error) => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens laden van projekten: "${error.message}"`, severity.error);
        });
    return projects ?? [];
}


const getUsers = async (): Promise<{uid: string, id: string}[]> => {
    const users = await FirestoreService.getCollection<{uid: string, id: string}>(`${firestoreCollections.users}`,
        (error) => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens laden van gebruikers: "${error.message}"`, severity.error);
        });
    return users ?? [];
}

const Admin = ({ user }) => {

    const [search, setSearch] = useState<string>('');
    const [users, setUsers] = useState<{id: string, uid: string}[]>();
    const [userData, setUserData] = useState<UserData>();
    const [projects, setProjects] = useState<Project[]>();
    const [sites, setSites] = useState<Site[]>();
    const [buildings, setBuildings] = useState<Building[]>();
    const [section, setSection] = useState<SectionJSON>(undefined);
    const [activeProject, setActiveProject] = useState<Project>();
    const [activeSite, setActiveSite] = useState<Site>();

    const loadUserData = async () => {
        const udp = await FirestoreService.getDoc(`users/${search}/data`, 'private', (err) => {
            console.error(err);
        })
        setUserData({ private: udp, public: undefined });
    }

    const loadProjectsForUser = async () => {
        const p = await getProjects(userData.private.userId);
        setProjects(p);
    }

    const loadSitesForUserAndProject = async (project: Project) => {
        setActiveProject(project);
        const s = await getSites(userData.private.userId, project.id);
        setSites(s);
    }

    const getBuildings = async (userId: string, projectId: number | string, siteId: number | string): Promise<Building[]> => {
        const buildings = await FirestoreService.getCollectionForUserId<Building>(`${firestoreCollections.projects}/${projectId}/${firestoreCollections.sites}/${siteId}/${firestoreCollections.buildings}`, userId,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens het laden van gebouwen: "${error.message}"`, severity.error);
            });
        ServiceManager.babylonService.markBuildingsAsDeprecated(buildings);
        ServiceManager.babylonService.mapPartsForBuildings(buildings);
        return buildings ?? [];
    }

    const fetchSection = async () => {
        const res = await CloudStorageService.downloadFile('section.json', (error) => {
            ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens laden van bestand section.json: "${error.message}"`, severity.error);
        });
        const data = await res.json();
        setSection(data);
        return data;
    };

    const saveProject = () => {
        const buildingsInSitesPromises = sites.map(s => {
            return getBuildings(userData.private.userId, activeProject.id, s.id);
        })
        Promise.all(buildingsInSitesPromises).then(siteBuildings => {
            const siteBuildingsFlat = siteBuildings.flat();
            const project = ServiceManager.buildingHelperService.includeAdditionalData(activeProject, sites, siteBuildingsFlat);
            let proj = JSON.stringify(project);
            ServiceManager.utilityService.download(activeProject.name + " " + activeProject.id, proj);
        })
    }

    const fetchConfig = async () => {
        const config = await FirestoreService.getDoc(firestoreCollections.config, process.env.REACT_APP_ENVIRONMENT as environment,
            (error) => {
                ServiceManager.snackbarService.openSnackbar(`Fout opgetreden tijdens laden van configuratie settings: "${error.message}"`, severity.error);
            });

        if (config) {
            Object.keys(config).forEach(key => {
                setEnvKey(process.env.REACT_APP_ENVIRONMENT as environment, key, config[key]);
            })
        }
    }


    useEffect(() => {
        const utilityService = new UtilityService();
        const geolocationHelper = new GeolocationHelper();
        const sceneManager = new SceneManager(utilityService, geolocationHelper);
        const mapService = new MapService();
        const buildingHelperService = new BuildingHelperService(utilityService);
        const babylonService = new BabylonService(sceneManager, buildingHelperService, mapService, utilityService);
        const httpService = new HttpService();

        ServiceManager.initServices(babylonService, sceneManager, buildingHelperService, utilityService, httpService);
        const d = async () => {
            await fetchSection();
            await fetchConfig();
        }
        d();
    }, [])

    useEffect(() => {
        if (section) {
            ServiceManager.babylonService.hybridBabylonService.setSection(section);
            ServiceManager.babylonService.corridorBabylonService.setSection(section);
            ServiceManager.babylonService.towerBabylonService.setSection(section);
            ServiceManager.babylonService.rowhouseBabylonService.setSection(section);
        }
    }, [section])

    return <div className={styles.Admin}>
        Welkom, admin {user.email}
        <div>
            <button onClick={async () => {
                const users = await getUsers();
                setUsers(users);
            }}>
                Get Users
            </button>
            {users && users.length > 0 && 
            <div  className={styles.group}>
                {users.map(u => {
                    return u.id
                }).join(', ')}
            </div>}
            <div>
                <label>
                    Search User Data By Email
                    <input onChange={(e) => { setSearch(e.target.value) }} />
                </label>
            </div>
            <button onClick={loadUserData}>Load User Data</button>
            {
                userData && userData.private &&
                <div>
                    <dl>
                        <dt>
                            UID
                        </dt>
                        <dd>
                            {userData.private.userId}
                        </dd>
                        <dt>
                            Naam
                        </dt>
                        <dd>
                            {userData.private.name}
                        </dd>
                        <dt>
                            Achternaam
                        </dt>
                        <dd>
                            {userData.private.surname}
                        </dd>
                        <dt>
                            Tussenvoegsel
                        </dt>
                        <dd>
                            {userData.private.addition}
                        </dd>
                        <dt>
                            Telefoon
                        </dt>
                        <dd>
                            {userData.private.phone}
                        </dd>
                        <dt>
                            Bedrijf
                        </dt>
                        <dd>
                            {userData.private.company}
                        </dd>
                        <dt>
                            Rollen
                        </dt>
                        <dd>
                            {userData.private.roles.join(', ')}
                        </dd>
                        <dt>
                            Gebruikersvoorwaarden en Privacy policy geaccepteerd
                        </dt>
                        <dd>
                            {userData.private.agreeToTermsAndConditionsAndPrivacy ? 'Ja' : 'Nee'}
                        </dd>
                        <dt>
                            Nieuwsbrief en offers geaccepteerd
                        </dt>
                        <dd>
                            {userData.private.agreeToReceiveEmailsWithNewsAndOffers ? 'Ja' : 'Nee'}
                        </dd>
                    </dl>
                    <div className={styles.group}>
                        <button onClick={loadProjectsForUser}>Load Projects</button>
                        {
                            projects && projects.length > 0 &&
                            <div>
                                {projects.map(p => <div key={p.id}>
                                    <pre className={styles.preview}>{JSON.stringify(p, null, 2)}</pre>
                                    <button onClick={() => loadSitesForUserAndProject(p)}>Load Sites</button>
                                    {
                                        sites && sites.length > 0 && <button onClick={saveProject}>Save Project</button>
                                    }
                                </div>)}
                            </div>
                        }
                    </div>
                    {activeProject && sites && sites.length > 0 &&
                        <div  className={styles.group}>
                            <h2>Sites for {activeProject.name}</h2>
                            <div>
                                {sites.map(s => {
                                    return <div key={s.id}>
                                        <pre className={styles.preview}>{JSON.stringify(s, null, 2)}</pre>
                                        <button onClick={async () => {
                                            setActiveSite(s);
                                            const b = await getBuildings(userData.private.userId, activeProject.id, s.id)
                                            setBuildings(b);
                                        }
                                        }>Load Buildings</button>
                                    </div>
                                })}
                            </div>
                        </div>
                    }
                    {activeProject && sites && sites.length > 0 && activeSite && buildings && buildings.length > 0 &&
                        <div className={styles.group}>
                            <h2>Buildings for {activeProject.name}</h2>
                            <div>
                                {buildings.map(b => {
                                    return <div key={b.id}>
                                        <pre className={styles.preview}>{JSON.stringify(b, null, 2)}</pre>
                                    </div>
                                })}
                            </div>
                        </div>
                    }
                </div>
            }
        </div>
    </div>
};

export default Admin;

